import { PrismicRichText } from 'prismic/types/richtext';
import {
  FormSlicesForm_Input,
  FormSlicesForm_InputSelectinputItems,
} from 'prismic-types';
import slugify from 'slugify';

import { Select, SelectOptions } from 'components/form-elements/Select';
import { TextArea } from 'components/form-elements/TextArea';
import { TextInput } from 'components/form-elements/TextInput';
import { asText } from 'components/rich-text/RichText';
import { IconList } from 'components/svg/Svg';
import { Upload } from 'containers/forms/Upload';

type BaseProps = {
  label: PrismicRichText;
  placeholder: PrismicRichText;
  name: PrismicRichText;
  required: boolean;
};

type FormInputProps = {
  type: string;
} & BaseProps;

type SelectProps = BaseProps & {
  options: SelectOptions;
};

type FileInputProps = Omit<BaseProps, 'placeholder' | 'name'> & {
  multiple?: boolean;
};

const formInputTypeToIcon = (type: string): IconList | undefined => {
  switch (type) {
    case 'email':
      return 'mail';
    case 'tel':
      return 'phone';
    case 'kennitala':
      return 'fingerprint';
    default:
      return undefined;
  }
};

const FormInputSlice = (props: FormInputProps) => {
  const { label, placeholder, name, required, type } = props;

  if (type === 'textarea') {
    return (
      <TextArea
        label={asText(label)}
        placeholder={asText(placeholder)}
        name={name ? slugify(asText(name)) : slugify(asText(label))}
        required={required}
      />
    );
  } else if (type === 'kennitala' || type === 'tel' || type === 'number') {
    const maxLength = {
      // allow +1 to the max length as we strip out ` ` and `-`
      kennitala: 11,
      tel: 8,
      number: undefined,
    };

    return (
      <TextInput
        label={asText(label)}
        placeholder={asText(placeholder)}
        name={name ? slugify(asText(name)) : slugify(asText(label))}
        required={required}
        type="tel"
        icon={formInputTypeToIcon(type)}
        maxLength={maxLength[type]}
      />
    );
  } else {
    return (
      <TextInput
        label={asText(label)}
        placeholder={asText(placeholder)}
        name={name ? slugify(asText(name)) : slugify(asText(label))}
        required={required}
        type={type}
        icon={formInputTypeToIcon(type)}
      />
    );
  }
};

const FileInputSlice = (props: FileInputProps) => {
  const { label, required, multiple } = props;

  return <Upload label={asText(label)} multiple={multiple} required={required} />;
};

const SelectInputSlice = (props: SelectProps) => {
  const { label, placeholder, name, options, required } = props;

  return (
    <Select
      label={asText(label)}
      placeholder={asText(placeholder)}
      name={name ? slugify(asText(name)) : slugify(asText(label))}
      required={required}
      options={options}
    />
  );
};

export function prismicSliceToTextInput(s: FormSlicesForm_Input) {
  if (!s.variation) {
    return null;
  }

  const label = s.variation.primary?.label as PrismicRichText;
  const name = s.variation.primary?.name as PrismicRichText;
  const required = s.variation.primary?.required as boolean;

  switch (s.variation.__typename) {
    case 'FormSlicesForm_inputDefault':
      return asText(label) ? (
        <FormInputSlice
          name={name}
          placeholder={s.variation.primary?.placeholder as PrismicRichText}
          label={label}
          type={s.variation.primary?.type ?? 'text'}
          required={required}
        />
      ) : (
        <div>Missing label for TextInput in Prismic</div>
      );
    case 'FormSlicesForm_inputFileinput':
      return (
        <FileInputSlice
          label={label}
          required={required}
          multiple={s.variation.primary?.multi ? true : false}
        />
      );
    case 'FormSlicesForm_inputSelectinput':
      return asText(label) ? (
        <SelectInputSlice
          name={name}
          placeholder={s.variation.primary?.placeholder as PrismicRichText}
          label={label}
          required={required}
          options={(
            (s.variation.items ?? []) as Array<FormSlicesForm_InputSelectinputItems>
          )
            .filter((item) => asText(item.label) !== '')
            .map((item) => {
              const optionLabel = asText(item.label as PrismicRichText);
              return {
                title: optionLabel,
                value: item.value ? asText(item.value as PrismicRichText) : optionLabel,
              };
            })}
        />
      ) : (
        <div>Missing label for TextInput in Prismic</div>
      );
  }
}
